import { useMemo, useState } from "react";
import { ToggleGroup, View } from "tamagui";

import { Text, getTokenValue } from "@/components";
import withTracking from "@/components/withTracking";

export type SegmentedControlOption<T extends string> = {
  value: T;
  label: string;
  disabled?: boolean;
};

type SegmentedControlProps<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedControlOption<T>[];
  dataTrackingId?: string;
};

const TrackedToggleGroup = withTracking(ToggleGroup, "toggleGroup");

/**
 * Rounded pill-shaped segmented control backed by Tamagui's ToggleGroup. Renders each option
 * inline; the selected pill slides between them on change. Callers are expected to keep the
 * option count small (2–3) since layout is measured once and split evenly.
 */
const inset = getTokenValue("$50");
const SegmentedControl = <T extends string>({
  value,
  onChange,
  options,
  dataTrackingId,
}: SegmentedControlProps<T>) => {
  const [segmentWidth, setSegmentWidth] = useState(0);
  const selectedIndex = options.findIndex((opt) => opt.value === value);
  const trackingOptions = useMemo(
    () => Object.fromEntries(options.map((opt) => [opt.value, opt.label])),
    [options],
  );

  return (
    <View
      borderWidth="$strokeWidth"
      borderColor="$strokeSubdued"
      borderRadius={9999}
      position="relative"
    >
      <View
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        borderRadius={9999}
        overflow="hidden"
        zIndex={0}
        onLayout={(e) => {
          setSegmentWidth(e.nativeEvent.layout.width / options.length);
        }}
      >
        <View
          position="absolute"
          top={inset}
          bottom={inset}
          width={segmentWidth - 2 * inset}
          borderRadius={9999}
          backgroundColor="$navSelected"
          transition="bouncy"
          animateOnly={["transform"]}
          x={inset + selectedIndex * segmentWidth}
        />
      </View>
      <TrackedToggleGroup
        type="single"
        value={value}
        onValueChange={(val: string) => {
          if (val) onChange(val as T);
        }}
        disableDeactivation
        unstyled
        flexDirection="row"
        dataTrackingId={dataTrackingId}
        options={trackingOptions}
      >
        {options.map((opt) => {
          const isSelected = value === opt.value;
          const isDisabled = Boolean(opt.disabled);

          return (
            <ToggleGroup.Item
              key={opt.value}
              value={opt.value}
              aria-label={opt.label}
              disabled={isDisabled}
              unstyled
              flex={1}
              flexBasis={0}
              justifyContent="center"
              alignItems="center"
              paddingVertical="$150"
              paddingHorizontal="$200"
              borderRadius={9999}
              outlineWidth={0}
              cursor={isDisabled ? "not-allowed" : "pointer"}
              opacity={isDisabled ? 0.5 : 1}
              focusStyle={{
                outlineColor: "$a11yBlue",
                outlineStyle: "solid",
                outlineWidth: 2,
              }}
              pressStyle={isDisabled ? undefined : { opacity: 0.6 }}
              zIndex={1}
            >
              <Text
                size="$sm"
                color={isSelected ? "$textPrimary" : "$textSecondary"}
                semibold={isSelected}
              >
                {opt.label}
              </Text>
            </ToggleGroup.Item>
          );
        })}
      </TrackedToggleGroup>
    </View>
  );
};

export default SegmentedControl;
